import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CardAuthenticationType } from '@finxone-platform/shared/sys-config-types';
import { finalize } from 'rxjs';
import { CardAuthenticationService } from '../../../lib/services/card-authentication-service/card-authentication.service';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-card-authentication-review',
  templateUrl: './card-authentication-review.component.html',
  styleUrl: './card-authentication-review.component.scss',
})
export class CardAuthenticationReviewComponent implements OnInit {
  authenticationId = '';
  authentication: CardAuthenticationType | null = null;
  isLoading = true;
  isSubmitting = false;
  errorMessage = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cardAuthenticationService: CardAuthenticationService,
  ) {}

  ngOnInit(): void {
    this.authenticationId = this.route.snapshot.paramMap.get('authenticationId') ?? '';
    this.loadAuthentication();
  }

  approve(): void {
    if (!this.authenticationId || this.isSubmitting || !this.isPending) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.cardAuthenticationService
      .approveCardAuthentication(this.authenticationId)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (authentication) => {
          this.authentication = authentication;
        },
        error: () => {
          this.errorMessage = 'Unable to approve this 3DS request right now.';
        },
      });
  }

  reject(): void {
    if (!this.authenticationId || this.isSubmitting || !this.isPending) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.cardAuthenticationService
      .rejectCardAuthentication(this.authenticationId)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (authentication) => {
          this.authentication = authentication;
        },
        error: () => {
          this.errorMessage = 'Unable to reject this 3DS request right now.';
        },
      });
  }

  get isPending(): boolean {
    return this.authentication?.status === 'pending';
  }

  get statusLabel(): string {
    switch (this.authentication?.status) {
      case 'accepted':
        return 'Approved';
      case 'rejected':
        return 'Declined';
      case 'cancelled':
        return 'Cancelled';
      case 'pending':
      default:
        return 'Pending';
    }
  }

  private loadAuthentication(): void {
    if (!this.authenticationId) {
      this.errorMessage = 'Missing authentication reference.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.cardAuthenticationService
      .getCardAuthentication(this.authenticationId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (authentication) => {
          this.authentication = authentication;
        },
        error: () => {
          this.errorMessage = 'Unable to load this 3DS request.';
        },
      });
  }
}
